import { useRef, useEffect, useCallback } from 'react';

export const JarvisEngine = ({ size = 400 }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  const draw = useCallback((ctx, w, h, t) => {
    const cx = w / 2;
    const cy = h / 2;
    const r = Math.min(w, h) * 0.38;

    ctx.clearRect(0, 0, w, h);

    // Central glow
    const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 1.3);
    glow.addColorStop(0, 'rgba(0,212,255,0.12)');
    glow.addColorStop(0.5, 'rgba(0,212,255,0.04)');
    glow.addColorStop(1, 'transparent');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, w, h);

    // Holographic wireframe sphere
    const lines = 12;
    ctx.strokeStyle = 'rgba(0,212,255,0.15)';
    ctx.lineWidth = 0.5;
    // Longitude lines (rotating)
    for (let i = 0; i < lines; i++) {
      const angle = (i / lines) * Math.PI + t * 0.3;
      ctx.beginPath();
      for (let j = 0; j <= 60; j++) {
        const phi = (j / 60) * Math.PI * 2;
        const x3d = Math.cos(angle) * Math.sin(phi);
        const z3d = Math.sin(angle) * Math.sin(phi);
        const y3d = Math.cos(phi);
        const scale = 1 / (1 + z3d * 0.3);
        const px = cx + x3d * r * 0.7 * scale;
        const py = cy + y3d * r * 0.7 * scale;
        if (j === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.stroke();
    }
    // Latitude lines
    for (let i = 1; i < 6; i++) {
      const phi = (i / 6) * Math.PI;
      const lr = Math.sin(phi) * r * 0.7;
      const ly = cy + Math.cos(phi) * r * 0.7;
      ctx.beginPath();
      ctx.ellipse(cx, ly, lr, lr * 0.3, t * 0.2, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Orbital rings (3 rings at different angles)
    const rings = [
      { tilt: 0.3, speed: 0.4, radius: r * 0.95, color: 'rgba(201,162,74,0.4)', dash: [8, 12] },
      { tilt: -0.5, speed: -0.3, radius: r * 1.05, color: 'rgba(0,212,255,0.35)', dash: [4, 8] },
      { tilt: 0.15, speed: 0.5, radius: r * 1.15, color: 'rgba(201,162,74,0.2)', dash: [2, 6] },
    ];
    rings.forEach(ring => {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(t * ring.speed);
      ctx.scale(1, ring.tilt);
      ctx.setLineDash(ring.dash);
      ctx.strokeStyle = ring.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(0, 0, ring.radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      // Orbiting dots on ring
      for (let d = 0; d < 3; d++) {
        const da = (d / 3) * Math.PI * 2 + t * ring.speed * 2;
        const dx = Math.cos(da) * ring.radius;
        const dy = Math.sin(da) * ring.radius;
        ctx.fillStyle = ring.color.replace(/[\d.]+\)/, '1)');
        ctx.beginPath();
        ctx.arc(dx, dy, 3, 0, Math.PI * 2);
        ctx.fill();
        // Dot glow
        const dotGlow = ctx.createRadialGradient(dx, dy, 0, dx, dy, 10);
        dotGlow.addColorStop(0, ring.color);
        dotGlow.addColorStop(1, 'transparent');
        ctx.fillStyle = dotGlow;
        ctx.fillRect(dx - 10, dy - 10, 20, 20);
      }
      ctx.restore();
    });

    // Scanning beam
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(t * 1.5);
    const beam = ctx.createLinearGradient(0, 0, r * 1.1, 0);
    beam.addColorStop(0, 'rgba(0,212,255,0.6)');
    beam.addColorStop(0.5, 'rgba(0,212,255,0.15)');
    beam.addColorStop(1, 'transparent');
    ctx.fillStyle = beam;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, r * 1.1, -0.04, 0.04);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Floating data particles
    for (let i = 0; i < 30; i++) {
      const seed = i * 137.508;
      const pr = r * 0.3 + r * 0.8 * ((Math.sin(seed + t * 0.5) + 1) / 2);
      const pa = seed + t * (0.1 + (i % 3) * 0.05);
      const px = cx + Math.cos(pa) * pr;
      const py = cy + Math.sin(pa) * pr * 0.6;
      const alpha = 0.2 + 0.5 * ((Math.sin(t * 2 + i) + 1) / 2);
      const sz = 1 + Math.sin(t + i) * 0.5;
      ctx.fillStyle = i % 2 === 0 
        ? `rgba(0,212,255,${alpha})` 
        : `rgba(201,162,74,${alpha * 0.7})`;
      ctx.beginPath();
      ctx.arc(px, py, sz, 0, Math.PI * 2);
      ctx.fill();
    }

    // Neural connection lines (between some particles)
    ctx.strokeStyle = 'rgba(0,212,255,0.06)';
    ctx.lineWidth = 0.5;
    for (let i = 0; i < 8; i++) {
      const s1 = i * 137.508;
      const s2 = (i + 3) * 137.508;
      const pr1 = r * 0.3 + r * 0.8 * ((Math.sin(s1 + t * 0.5) + 1) / 2);
      const pa1 = s1 + t * (0.1 + (i % 3) * 0.05);
      const pr2 = r * 0.3 + r * 0.8 * ((Math.sin(s2 + t * 0.5) + 1) / 2);
      const pa2 = s2 + t * (0.1 + ((i + 3) % 3) * 0.05);
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(pa1) * pr1, cy + Math.sin(pa1) * pr1 * 0.6);
      ctx.lineTo(cx + Math.cos(pa2) * pr2, cy + Math.sin(pa2) * pr2 * 0.6);
      ctx.stroke();
    }

    // Hexagonal HUD elements
    const hexPositions = [
      { angle: t * 0.2, dist: r * 0.85, size: 6 },
      { angle: t * 0.2 + 2.09, dist: r * 0.85, size: 6 },
      { angle: t * 0.2 + 4.19, dist: r * 0.85, size: 6 },
    ];
    hexPositions.forEach(hex => {
      const hx = cx + Math.cos(hex.angle) * hex.dist;
      const hy = cy + Math.sin(hex.angle) * hex.dist * 0.5;
      ctx.strokeStyle = 'rgba(201,162,74,0.4)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let v = 0; v < 6; v++) {
        const va = (v / 6) * Math.PI * 2 - Math.PI / 6;
        const vx = hx + Math.cos(va) * hex.size;
        const vy = hy + Math.sin(va) * hex.size;
        if (v === 0) ctx.moveTo(vx, vy);
        else ctx.lineTo(vx, vy);
      }
      ctx.closePath();
      ctx.stroke();
    });

    // Pulsing center circle
    const pulse = 1 + Math.sin(t * 3) * 0.08;
    ctx.strokeStyle = 'rgba(0,212,255,0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.55 * pulse, 0, Math.PI * 2);
    ctx.stroke();

    // Inner pulsing circle
    ctx.strokeStyle = 'rgba(201,162,74,0.25)';
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.4 * pulse, 0, Math.PI * 2);
    ctx.stroke();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = size;
    const h = size;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    let startTime = performance.now();
    const animate = (now) => {
      const t = (now - startTime) / 1000;
      draw(ctx, w, h, t);
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [size, draw]);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* Canvas animation background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{ width: size, height: size }}
      />
      {/* Brain video overlay - centered with glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="relative overflow-hidden rounded-full"
          style={{
            width: size * 0.55,
            height: size * 0.55,
            animation: 'brainFloat 4s ease-in-out infinite',
          }}
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            style={{
              filter: 'drop-shadow(0 0 25px rgba(0,212,255,0.6)) drop-shadow(0 0 50px rgba(0,212,255,0.3))',
              animation: 'brainPulse 3s ease-in-out infinite',
            }}
          >
            <source src="/assets/brain-video.mp4" type="video/mp4" />
            {/* Fallback to image if video fails */}
            <img src="/assets/brain-ai.png" alt="AI Brain Engine" className="w-full h-full object-contain" />
          </video>
          {/* Holographic overlay */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'radial-gradient(circle, transparent 40%, rgba(5,6,8,0.4) 70%, rgba(5,6,8,0.8) 100%)',
            }}
          />
        </div>
      </div>
      <style>{`
        @keyframes brainFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes brainPulse {
          0%, 100% { opacity: 0.9; filter: drop-shadow(0 0 25px rgba(0,212,255,0.6)) drop-shadow(0 0 50px rgba(0,212,255,0.3)); }
          50% { opacity: 1; filter: drop-shadow(0 0 35px rgba(0,212,255,0.8)) drop-shadow(0 0 70px rgba(0,212,255,0.4)); }
        }
        @keyframes holoShift {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default JarvisEngine;
